import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import QuickLogModal from './QuickLogModal';
import type { Outreach } from '../types/api';

// Lifts the QuickLog FAB + modal out of Dashboard so any primary page
// can trigger it (via useQuickLog().open) and react to a successful log
// (via useQuickLog().onLogged subscriber).

type QuickKind = 'initial' | 'follow_up' | undefined;

interface QuickLogContextValue {
  open: (kind?: QuickKind) => void;
  onLogged: (handler: (outreach: Outreach) => void) => () => void;
}

const QuickLogContext = createContext<QuickLogContextValue | null>(null);

export function useQuickLog(): QuickLogContextValue {
  const ctx = useContext(QuickLogContext);
  if (!ctx) {
    throw new Error('useQuickLog must be used inside <QuickLogProvider>');
  }
  return ctx;
}

interface ProviderProps {
  children: ReactNode;
}

export function QuickLogProvider({ children }: ProviderProps) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<QuickKind>(undefined);
  const subscribers = useRef<Set<(outreach: Outreach) => void>>(new Set());

  const openWith = useCallback((k?: QuickKind) => {
    setKind(k);
    setOpen(true);
  }, []);

  const onLogged = useCallback((handler: (outreach: Outreach) => void) => {
    subscribers.current.add(handler);
    return () => {
      subscribers.current.delete(handler);
    };
  }, []);

  const value = useMemo<QuickLogContextValue>(
    () => ({ open: openWith, onLogged }),
    [openWith, onLogged],
  );

  return (
    <QuickLogContext.Provider value={value}>
      {children}
      <QuickLogModal
        open={open}
        defaultKindShortName={kind}
        onClose={() => setOpen(false)}
        onSuccess={(outreach) => {
          for (const handler of subscribers.current) {
            try {
              handler(outreach);
            } catch (e) {
              // Subscriber failures shouldn't prevent other pages from
              // refreshing; surface in console only.
              console.error('QuickLog subscriber failed', e);
            }
          }
        }}
      />
    </QuickLogContext.Provider>
  );
}